import React from 'react';
import { Container, Card, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getToolsDashboardMetrics } from '../../services/toolDashboardService';
import { returnTool } from '../../services/toolAssignmentService';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Admin dashboard for the tool module (#95). Mobile-first per D9 —
 * widgets stack vertically on narrow viewports, no recharts (kept light;
 * we render simple horizontal bars via div widths).
 */
const ToolsDashboardPage = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tools-dashboard'],
    queryFn: () => getToolsDashboardMetrics({ window_days: 30 }),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000
  });

  const forceReturnMutation = useMutation({
    mutationFn: ({ tool_id }) =>
      returnTool({ tool_id, notes: 'Forzado desde dashboard de admin' }),
    onSuccess: ({ error: err }) => {
      if (err) { toast.error(err.message); return; }
      toast.success('Devolución forzada.');
      queryClient.invalidateQueries({ queryKey: ['tools-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
    onError: (e) => toast.error(e.message)
  });

  if (!isAdmin) {
    return <Alert variant="danger" className="m-3">Acceso restringido a administradores.</Alert>;
  }

  if (isLoading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-3 px-3">
        <Alert variant="danger">No se pudo cargar el dashboard: {error.message}</Alert>
      </Container>
    );
  }

  const m = data?.data;
  if (!m) {
    return (
      <Container fluid className="py-3 px-3">
        <Alert variant="warning">Sin datos.</Alert>
      </Container>
    );
  }

  return (
    <DashboardLayout>
    <Container fluid className="py-3 px-3" style={{ maxWidth: 1100 }}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button as={Link} to="/herramientas" variant="link" className="p-0" aria-label="Volver">
            <i className="bi bi-arrow-left fs-4" />
          </Button>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-bar-chart" />
            Dashboard de herramientas
          </h4>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => refetch()}>
          <i className="bi bi-arrow-clockwise me-1" />Actualizar
        </Button>
      </div>

      <StatCards totals={m.totals} avgDays={m.avg_assignment_days} />

      <div className="d-flex flex-column gap-3 mt-3">
        <WeeklyChart weeks={m.weekly_assignments} />
        <TopHoldersList holders={m.top_holders} />
        <TopDamagedList damaged={m.top_damaged} />
        <ForgottenList
          forgotten={m.forgotten_assignments}
          onForce={(a) => forceReturnMutation.mutate({ tool_id: a.tool_id })}
          pending={forceReturnMutation.isPending}
        />
      </div>
    </Container>
    </DashboardLayout>
  );
};

const StatCard = ({ icon, label, value, variant, link }) => (
  <Card
    as={link ? Link : 'div'}
    to={link}
    className="modern-card text-decoration-none"
    style={{ borderRadius: 12, minHeight: 88 }}
  >
    <Card.Body className="d-flex align-items-center gap-3 py-3">
      <div className={`text-${variant || 'primary'}`} style={{ fontSize: '1.75rem', lineHeight: 1 }}>
        <i className={`bi ${icon}`} />
      </div>
      <div>
        <div className="fs-4 fw-bold" style={{ lineHeight: 1.1 }}>{value}</div>
        <small className="text-muted">{label}</small>
      </div>
    </Card.Body>
  </Card>
);

const StatCards = ({ totals, avgDays }) => (
  <div className="row g-2">
    <div className="col-6 col-md-3"><StatCard icon="bi-tools" label="Total" value={totals.all} variant="primary" link="/herramientas" /></div>
    <div className="col-6 col-md-3"><StatCard icon="bi-person-check" label="Asignadas" value={totals.assigned} variant="info" /></div>
    <div className="col-6 col-md-3"><StatCard icon="bi-shop" label="En bodega" value={totals.available} variant="success" /></div>
    <div className="col-6 col-md-3"><StatCard icon="bi-tools" label="Mantto." value={totals.maintenance} variant="warning" /></div>
    {totals.lost > 0 && (
      <div className="col-6 col-md-3"><StatCard icon="bi-exclamation-triangle" label="Perdidas" value={totals.lost} variant="danger" /></div>
    )}
    <div className="col-6 col-md-3"><StatCard icon="bi-clock-history" label="Días promedio" value={avgDays || 0} variant="secondary" /></div>
  </div>
);

const WeeklyChart = ({ weeks }) => {
  if (!weeks || weeks.length === 0) {
    return (
      <Card className="modern-card">
        <Card.Body><small className="text-muted">Sin asignaciones recientes.</small></Card.Body>
      </Card>
    );
  }
  const max = Math.max(...weeks.map((w) => w.count), 1);
  return (
    <Card className="modern-card">
      <Card.Body>
        <strong className="d-block mb-2">Asignaciones por semana (últimas 12)</strong>
        <div className="d-flex align-items-end gap-1" style={{ height: 100 }}>
          {weeks.map((w) => {
            const h = Math.max(4, (w.count / max) * 90);
            return (
              <div
                key={w.week}
                style={{ flex: 1, height: h, background: 'var(--bs-primary)', opacity: 0.55, borderRadius: 4 }}
                title={`${w.week}: ${w.count}`}
                aria-label={`Semana ${w.week}: ${w.count} asignaciones`}
              />
            );
          })}
        </div>
        <div className="d-flex justify-content-between mt-1">
          <small className="text-muted">{weeks[0]?.week}</small>
          <small className="text-muted">{weeks[weeks.length - 1]?.week}</small>
        </div>
      </Card.Body>
    </Card>
  );
};

const TopHoldersList = ({ holders }) => (
  <Card className="modern-card">
    <Card.Body>
      <strong className="d-block mb-2">Top holders (últimos 30 días)</strong>
      {(!holders || holders.length === 0) && <small className="text-muted">Sin asignaciones en el período.</small>}
      {holders && holders.map((h) => (
        <div key={h.holder_id} className="d-flex align-items-center gap-2 mb-1">
          <span className="text-truncate" style={{ flex: 1, minWidth: 0 }}>{h.name}</span>
          <div className="flex-grow-1" style={{ height: 8, background: '#f0f0f0', borderRadius: 4, position: 'relative', maxWidth: 160 }}>
            <div style={{ width: `${Math.min(100, (h.count / Math.max(1, holders[0].count)) * 100)}%`, height: 8, background: 'var(--bs-info)', borderRadius: 4 }} />
          </div>
          <Badge bg="info">{h.count}</Badge>
        </div>
      ))}
    </Card.Body>
  </Card>
);

const TopDamagedList = ({ damaged }) => (
  <Card className="modern-card">
    <Card.Body>
      <strong className="d-block mb-2">Más reportadas como dañadas (12 meses)</strong>
      {(!damaged || damaged.length === 0) && <small className="text-muted">Sin reportes de daño en el período. 🎉</small>}
      {damaged && damaged.map((d) => (
        <div key={d.tool_id} className="d-flex align-items-center justify-content-between mb-1">
          <Link to={`/herramientas/${d.tool_id}`} className="text-truncate" style={{ flex: 1, minWidth: 0 }}>
            <strong>{d.name || '—'}</strong>{' '}
            <small className="text-muted font-monospace">{d.serial_number}</small>
          </Link>
          <Badge bg="warning" text="dark">{d.count} ×</Badge>
        </div>
      ))}
    </Card.Body>
  </Card>
);

const ForgottenList = ({ forgotten, onForce, pending }) => (
  <Card className="modern-card">
    <Card.Body>
      <strong className="d-block mb-2">
        <i className="bi bi-exclamation-circle text-warning me-1" />
        Asignaciones olvidadas (más de 30 días)
      </strong>
      {(!forgotten || forgotten.length === 0) && <small className="text-muted">Ninguna. Todo bajo control. 👍</small>}
      {forgotten && forgotten.map((a) => (
        <div key={a.assignment_id} className="d-flex flex-wrap align-items-center gap-2 py-2 border-bottom">
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Link to={`/herramientas/${a.tool_id}`} className="text-decoration-none">
              <strong>{a.tool_name || '—'}</strong>{' '}
              <small className="text-muted font-monospace">{a.serial_number}</small>
            </Link>
            <div className="small text-muted">
              con <strong>{a.holder_name || 'desconocido'}</strong> · {a.days_open} días
            </div>
          </div>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => onForce(a)}
            disabled={pending}
          >
            <i className="bi bi-box-arrow-in-left me-1" />
            Forzar devolución
          </Button>
        </div>
      ))}
    </Card.Body>
  </Card>
);

export default ToolsDashboardPage;
